import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, ArrowRight } from "lucide-react";
import { SITE } from "@/lib/site";
import { getApiBase, getWebsiteLeadSourceHeaders } from "@/lib/apiBase";

export default function Contact() {
  const [form, setForm] = React.useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = React.useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.message) {
      toast.error("Please complete all fields");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${getApiBase()}/contact`, form, { headers: getWebsiteLeadSourceHeaders() });
      toast.success("Message sent! We reply within 24 hours.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error("Couldn't send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const tiles = [
    { Icon: MapPin, label: "Address", value: SITE.address, href: SITE.mapsLink },
    { Icon: Phone, label: "Phone / WhatsApp", value: SITE.phoneDisplay, href: `tel:${SITE.phoneTel}` },
    { Icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}` },
  ];

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#0b1d2a] text-white relative overflow-hidden">
      <div className="absolute -top-32 -left-20 w-[480px] h-[480px] rounded-full bg-[#0ea5e9]/20 blur-3xl" />
      <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-[#0369a1]/30 blur-3xl" />

      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow text-sky-300">Contact</div>
            <h2 className="serif h-display mt-4 text-4xl sm:text-5xl lg:text-6xl">
              Let's talk water, <br />
              <span className="italic text-sky-300">logistics & growth.</span>
            </h2>
          </div>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-5">
          <div className="lg:col-span-5 grid gap-4">
            {tiles.map((t, i) => (
              <a
                key={i}
                href={t.href}
                target={t.label === "Address" ? "_blank" : undefined}
                rel="noreferrer"
                data-testid={`contact-tile-${t.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="rounded-3xl bg-white/[0.04] border border-white/10 p-6 hover:bg-white/[0.08] transition group"
              >
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-full bg-sky-300/10 border border-sky-300/30 flex items-center justify-center text-sky-300 group-hover:scale-110 transition-transform">
                    <t.Icon size={18} />
                  </div>
                  <div>
                    <div className="eyebrow text-sky-300/80">{t.label}</div>
                    <div className="mt-2 serif text-lg leading-snug">{t.value}</div>
                  </div>
                </div>
              </a>
            ))}
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 rounded-3xl bg-white text-[#0b1d2a] p-7 md:p-10"
          >
            <div className="eyebrow">Send us a message</div>
            <h3 className="serif h-display mt-2 text-2xl sm:text-3xl">We reply within 24 hours.</h3>

            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              <Field label="Name" id="c-name">
                <input
                  id="c-name" data-testid="contact-input-name" required
                  value={form.name} onChange={set("name")}
                  className="w-full bg-transparent border-0 border-b border-sky-200 focus:border-[#0369a1] focus:ring-0 outline-none py-2"
                />
              </Field>
              <Field label="Phone" id="c-phone">
                <input
                  id="c-phone" data-testid="contact-input-phone" required
                  value={form.phone} onChange={set("phone")}
                  className="w-full bg-transparent border-0 border-b border-sky-200 focus:border-[#0369a1] focus:ring-0 outline-none py-2"
                />
              </Field>
              <Field label="Email" id="c-email" full>
                <input
                  id="c-email" type="email" data-testid="contact-input-email" required
                  value={form.email} onChange={set("email")}
                  className="w-full bg-transparent border-0 border-b border-sky-200 focus:border-[#0369a1] focus:ring-0 outline-none py-2"
                />
              </Field>
              <Field label="Message" id="c-message" full>
                <textarea
                  id="c-message" data-testid="contact-input-message" rows={4} required
                  value={form.message} onChange={set("message")}
                  className="w-full bg-transparent border-0 border-b border-sky-200 focus:border-[#0369a1] focus:ring-0 outline-none py-2 resize-none"
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0b1d2a] text-white px-7 py-3.5 font-semibold hover:bg-[#0369a1] transition disabled:opacity-60"
            >
              {submitting ? "Sending..." : "Send Message"} <ArrowRight size={16} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, id, full, children }) {
  return (
    <label htmlFor={id} className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
      {children}
    </label>
  );
}
